import { InputHTMLAttributes, RefObject } from 'react'

import { useUnitTranslate } from 'aem-libs-react-ts/src/hooks/useUnitTranslate'
import { FormatFnc } from 'aem-libs-react-ts/src/utils/Formatters'
import { InputUnitType } from 'aem-libs-react-ts/src/utils/unitType'
import { inputId } from 'aem-libs-tests/src/constants/inputId'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'

import { CommonInputWrapper } from '../CommonInputWrapper'
import * as InputStyles from '../input.styled'
import { CommonInputProps } from '../types'
import { useFieldError } from '../useFieldError'
import { useFieldMeta } from '../useFieldMeta'
import { ValidationMessage } from '../validation-message'
import { useNumberField } from './useNumberField'


interface HtmlInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'min' | 'max' | 'onChange' | 'value' | 'width'> {
  onChange?: (value: number) => void
  min?: number
  max?: number
}

export interface NumberInputRef {
  setValue: (value: number) => void
}

interface BaseNumberFieldProps extends CommonInputProps, HtmlInputProps {
  outerId?: string
  format?: FormatFnc
  useDelayedChange?: boolean
  unitType?: InputUnitType
  unitCustom?: string
  enableNull?: boolean
  ref?: RefObject<NumberInputRef | null>
}

interface RequiredValueProps {
  value: number
  onChangeComplete?: (value: number) => void
  mode?: 'default'
}

interface OptionalValueProps {
  value: number | null
  onChangeComplete?: (value: number | null) => void
  mode: 'nullable'
}

type ValueProps = RequiredValueProps | OptionalValueProps

export type NumberFieldProps = BaseNumberFieldProps & ValueProps

const defaultValueUnit = 5

export const NumberField = ({
  className,
  min,
  max,
  useDelayedChange,
  format,
  onChangeComplete,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mode = 'default',
  onChange,
  onBlur,
  onFocus,
  outerId,
  value,
  type = 'text',
  label,
  width,
  unitType,
  unitCustom,
  validationMessage,
  enableNull,
  tip,
  trackingDescription,
  hideValidation,
  responsive,
  testId,
  ref,
  ...inputProps
}: NumberFieldProps) => {
  const { inputRef, handleBlur, handleFocus, handleKeyDown, handleChange, handleKeyUp } = useNumberField(
    value,
    {
      min,
      max,
      format,
      useDelayedChange,
      enableNull,
      ref
    },
    {
      onChangeComplete,
      onChange,
      onBlur,
      onFocus
    },
    {
      description: trackingDescription ?? label,
      id: inputProps.id
    }
  )

  const unitLabel = useUnitTranslate(value ?? defaultValueUnit, unitType, unitCustom)
  const { showError, ariaErrorProps, errorMessage } = useFieldError(inputProps.id, validationMessage, {
    hideValidation
  })
  const fieldProps = useFieldMeta(inputProps.id, { tip: tip, unit: unitType }, ariaErrorProps)
  const usedTestId = testId ?? 'numberfield'

  return (
    <CommonInputWrapper id={inputProps.id} htmlFor={inputProps.id} label={label} tip={tip} testId={usedTestId}>
      <InputStyles.Wrapper>
        <InputStyles.InputContainer
          id={outerId}
          className={className}
          disabled={inputProps.disabled}
          width={width}
          responsive={responsive}
        >
          <InputStyles.Input
            ref={inputRef}
            hasUnit={!!(unitCustom || unitType)}
            data-testid={createTestId(usedTestId, inputId.input)}
            type={type}
            inputMode={'numeric'}
            disabled={inputProps.disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            onKeyUp={handleKeyUp}
            {...inputProps}
            {...fieldProps}
          />
          {(unitCustom || unitType) && (
            <InputStyles.Unit
              id={`${inputProps.id}-unit`}
              data-testid={createTestId(usedTestId, inputId.unit)}
              disabled={inputProps.disabled}
            >
              {unitLabel}
            </InputStyles.Unit>
          )}
        </InputStyles.InputContainer>
        {showError && <ValidationMessage id={inputProps.id} message={errorMessage} testId={usedTestId} label={label} />}
      </InputStyles.Wrapper>
    </CommonInputWrapper>
  )
}
